'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface MarketCard {
  id: string;
  title: string;
  value: string;
  change: number;
  changePercent: number;
  icon: string;
}

interface NewsCard {
  id: string;
  title: string;
  source: string;
  time: string;
  image: string;
  alt: string;
  category: string;
}

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const marketCards: MarketCard[] = [
  {
    id: 'market_nifty',
    title: 'NIFTY 50',
    value: '21,823.45',
    change: 125.30,
    changePercent: 0.58,
    icon: 'ChartBarIcon'
  },
  {
    id: 'market_gold',
    title: 'GOLD (10g)',
    value: '₹62,450',
    change: 180.00,
    changePercent: 0.29,
    icon: 'CurrencyRupeeIcon'
  },
  {
    id: 'market_usdinr',
    title: 'USD/INR',
    value: '83.12',
    change: 0.08,
    changePercent: 0.10,
    icon: 'BanknotesIcon'
  }];


  const featuredNews: NewsCard[] = [
  {
    id: 'news_1',
    title: 'RBI Holds Repo Rate Steady at 6.5%, Focuses on Inflation Management',
    source: 'Economic Times',
    time: '2 hours ago',
    image: "https://images.unsplash.com/photo-1648630762911-ccfd651d128c",
    alt: 'Reserve Bank of India building exterior with Indian flag',
    category: 'Policy'
  },
  {
    id: 'news_2',
    title: 'Nifty Hits New High as IT Stocks Rally on Strong Q4 Earnings',
    source: 'Moneycontrol',
    time: '4 hours ago',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce29ad7a-1767117541696.png",
    alt: 'Stock market trading floor with digital screens showing green upward trends',
    category: 'Stocks'
  }];


  const trendingTopics = [
  { id: 'trend_1', label: 'RBI Policy', count: 1240 },
  { id: 'trend_2', label: 'IT Earnings', count: 980 },
  { id: 'trend_3', label: 'Gold Rally', count: 756 },
  { id: 'trend_4', label: 'IPO News', count: 634 }];


  return (
    <section className="container mx-auto px-4 py-20 max-w-7xl">
      {/* Hero Text */}
      <div className="text-center mb-12 fade-in-up">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 font-display tracking-tight relative z-10">
          <span className="bg-background/95 backdrop-blur-sm px-4 py-2 rounded-lg inline-block shadow-sm text-[rgba(80,78,78,1)]">
            Your Financial Intelligence.{' '}
          </span>
          <span className="text-gradient-primary block mt-2">All in One Place.</span>
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Real-time market data, curated news, and commodity rates - automatically 
          collected from trusted sources. Stay ahead of the market, every day.
        </p>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Live Market Cards */}
        {marketCards.map((card, index) =>
        <div
          key={card.id}
          className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-all hover:scale-105"
          style={{ animationDelay: `${index * 0.1}s` }}>
          
            <div className="flex items-center justify-between mb-4">
              <Icon
              name={card.icon as any}
              size={24}
              className="text-primary" />
            
              <div className="w-2 h-2 bg-primary rounded-full pulse-glow" />
            </div>
            <h3 className="text-sm text-muted-foreground mb-2">{card.title}</h3>
            <p className="text-2xl font-bold text-foreground font-mono mb-2">
              {card.value}
            </p>
            <div
            className={`flex items-center gap-1 text-sm font-mono ${
            card.change >= 0 ? 'text-primary' : 'text-secondary'}`
            }>
            
              <Icon
              name={card.change >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
              size={16}
              variant="solid" />
            
              <span>{card.changePercent.toFixed(2)}%</span>
            </div>
          </div>
        )}

        {/* Featured News Card (Large) */}
        <div className="lg:col-span-2 lg:row-span-2 bg-card border border-border rounded-xl overflow-hidden hover:border-primary transition-all group">
          <Link href="/news" className="block">
            <div className="relative h-64 overflow-hidden">
              <AppImage
                src={featuredNews[0].image}
                alt={featuredNews[0].alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  {featuredNews[0].category}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                {featuredNews[0].title}
              </h3>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{featuredNews[0].source}</span>
                <span>•</span>
                <span>{featuredNews[0].time}</span>
              </div>
            </div>
          </Link>
        </div>

        {/* Trending Topics Card */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="FireIcon" size={20} className="text-accent" variant="solid" />
            Trending Now
          </h3>
          <div className="flex flex-wrap gap-3">
            {trendingTopics.map((topic) =>
            <button
              key={topic.id}
              className="px-4 py-2 bg-muted hover:bg-primary hover:text-primary-foreground rounded-full text-sm font-medium text-foreground transition-all">
              
                {topic.label}
                <span className="ml-2 text-xs text-muted-foreground">
                  {topic.count}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Secondary News Card */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden hover:border-primary transition-all group">
          <Link href="/news" className="flex h-full">
            <div className="relative w-1/3 overflow-hidden">
              <AppImage
                src={featuredNews[1].image}
                alt={featuredNews[1].alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              
            </div>
            <div className="flex-1 p-6 flex flex-col justify-center">
              <span className="px-3 py-1 bg-accent/20 text-accent text-xs font-semibold rounded-full w-fit mb-3">
                {featuredNews[1].category}
              </span>
              <h3 className="text-base font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                {featuredNews[1].title}
              </h3>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{featuredNews[1].source}</span>
                <span>•</span>
                <span>{featuredNews[1].time}</span>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          href="/markets"
          className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 flex items-center gap-2">
          
          Explore Markets
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
        <Link
          href="/news"
          className="px-8 py-4 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80 transition-all hover:scale-105 flex items-center gap-2">
          
          Latest News
          <Icon name="NewspaperIcon" size={20} />
        </Link>
      </div>
    </section>);

}